import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight, Eye, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useVideoTutorial } from '@/context/VideoTutorialContext';
import {
  StepsPlayer,
  YouTubePlayerWithComments,
  isStepsLikeTutorial,
  isLinkTutorial,
  getTutorialExternalLink,
} from '@/components/HelpPanel';

/**
 * Affichage d’un tutoriel dans un onglet / fenêtre séparée (ouverture depuis le centre d’aide).
 */
export default function HelpTutorialWindow() {
  const { tutorialId } = useParams();
  const [searchParams] = useSearchParams();
  const decodedId = tutorialId ? decodeURIComponent(tutorialId) : '';

  const stepParam = searchParams.get('step');
  const parsedStep =
    stepParam != null && stepParam !== '' ? parseInt(stepParam, 10) : NaN;
  const stepOneBased =
    Number.isFinite(parsedStep) && parsedStep >= 1 ? parsedStep : 1;
  const initialStepIndex = stepOneBased - 1;

  const {
    preloadHelpData,
    playTutorial,
    currentVideo,
    closeVideoPlayer,
    markAsViewed,
    getVisibleThemes,
    isTutorialViewed,
    playlist,
    playlistIndex,
    nextInPlaylist,
  } = useVideoTutorial();

  const [stepNavInfo, setStepNavInfo] = useState({ current: 1, total: 1, isLast: true });
  const [stepMeta, setStepMeta] = useState({
    current: stepOneBased,
    total: 0,
    description: '',
  });
  const [loadError, setLoadError] = useState(null);
  /** Après « Parcours terminé », le contexte vide currentVideo : sans cet état, l’UI retombait sur le spinner indéfiniment. */
  const [tutorialFinished, setTutorialFinished] = useState(false);
  /** Fermeture automatique de l’onglet en cours (repli si le navigateur refuse window.close). */
  const [autoClosePending, setAutoClosePending] = useState(false);
  const autoCloseFallbackTimerRef = useRef(null);
  const stepsPlayerRef = useRef(null);

  const visibleThemes = useMemo(() => getVisibleThemes(), [getVisibleThemes]);

  useEffect(() => {
    let cancelled = false;
    if (autoCloseFallbackTimerRef.current) {
      clearTimeout(autoCloseFallbackTimerRef.current);
      autoCloseFallbackTimerRef.current = null;
    }
    (async () => {
      setTutorialFinished(false);
      setAutoClosePending(false);
      setLoadError(null);
      if (!decodedId) {
        setLoadError('Tutoriel introuvable.');
        return;
      }
      try {
        await preloadHelpData();
        if (cancelled) return;
        await playTutorial({ id: decodedId });
      } catch (e) {
        console.error(e);
        if (!cancelled) setLoadError('Impossible de charger le tutoriel.');
      }
    })();
    return () => {
      cancelled = true;
      if (autoCloseFallbackTimerRef.current) {
        clearTimeout(autoCloseFallbackTimerRef.current);
        autoCloseFallbackTimerRef.current = null;
      }
      closeVideoPlayer();
    };
  }, [decodedId, preloadHelpData, playTutorial, closeVideoPlayer]);

  useEffect(() => {
    if (!currentVideo) {
      setStepNavInfo({ current: 1, total: 1, isLast: true });
      return;
    }
    if (isLinkTutorial(currentVideo)) {
      const href = getTutorialExternalLink(currentVideo);
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
        void markAsViewed(currentVideo.id);
      }
      closeVideoPlayer();
      setTutorialFinished(true);
      setAutoClosePending(true);
      try {
        window.close();
      } catch (_) {
        /* ignore */
      }
      return;
    }
    if (isStepsLikeTutorial(currentVideo)) return;
    if (playlist?.length > 1) {
      setStepNavInfo({
        current: playlistIndex + 1,
        total: playlist.length,
        isLast: playlistIndex >= playlist.length - 1,
      });
    } else {
      setStepNavInfo({ current: 1, total: 1, isLast: false });
    }
  }, [currentVideo, playlist, playlistIndex, markAsViewed, closeVideoPlayer]);

  const currentThemeLabel = useMemo(() => {
    if (!currentVideo?.theme_id) return '';
    const t = visibleThemes.find((th) => th.id === currentVideo.theme_id);
    return t?.nom || '';
  }, [currentVideo, visibleThemes]);

  const handleVideoComplete = useCallback(() => {
    const tutorialIdToMark = currentVideo?.id ?? null;
    const hadPlaylist = !!playlist;

    void (async () => {
      if (tutorialIdToMark) {
        await markAsViewed(tutorialIdToMark);
      }
      if (hadPlaylist) {
        const hasNext = nextInPlaylist();
        if (hasNext) return;
      }

      if (autoCloseFallbackTimerRef.current) {
        clearTimeout(autoCloseFallbackTimerRef.current);
        autoCloseFallbackTimerRef.current = null;
      }

      setAutoClosePending(true);
      closeVideoPlayer();
      requestAnimationFrame(() => {
        window.close();
      });
      autoCloseFallbackTimerRef.current = window.setTimeout(() => {
        autoCloseFallbackTimerRef.current = null;
        setAutoClosePending(false);
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          setTutorialFinished(true);
        }
      }, 850);
    })();
  }, [currentVideo, playlist, markAsViewed, nextInPlaylist, closeVideoPlayer]);

  const footerPrevDisabled =
    !currentVideo ||
    !isStepsLikeTutorial(currentVideo) ||
    (stepNavInfo?.current ?? 1) <= 1;

  const handleFooterPrev = () => {
    if (!currentVideo || !isStepsLikeTutorial(currentVideo)) return;
    stepsPlayerRef.current?.prevStep?.();
  };

  const handleFooterNext = () => {
    if (!currentVideo) return;
    if (isStepsLikeTutorial(currentVideo)) {
      stepsPlayerRef.current?.nextStep?.();
      return;
    }
    if (playlist && playlist.length > 1 && playlistIndex < playlist.length - 1) {
      nextInPlaylist();
      return;
    }
    handleVideoComplete();
  };

  const handleLeave = useCallback(() => {
    if (autoCloseFallbackTimerRef.current) {
      clearTimeout(autoCloseFallbackTimerRef.current);
      autoCloseFallbackTimerRef.current = null;
    }
    setAutoClosePending(false);
    setTutorialFinished(false);
    closeVideoPlayer();
    window.close();
  }, [closeVideoPlayer]);

  const tutorialNavRef = useRef({
    handleFooterPrev,
    handleFooterNext,
    footerPrevDisabled,
  });
  tutorialNavRef.current = {
    handleFooterPrev,
    handleFooterNext,
    footerPrevDisabled,
  };

  useEffect(() => {
    if (loadError) return;
    if (!currentVideo || String(currentVideo.id) !== String(decodedId)) return;

    const onKeyDown = (e) => {
      const el = e.target;
      if (
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        const { handleFooterPrev: prev, footerPrevDisabled: prevDisabled } = tutorialNavRef.current;
        if (prevDisabled) return;
        e.preventDefault();
        prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        tutorialNavRef.current.handleFooterNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loadError, currentVideo, decodedId]);

  const renderHeaderNav = () => {
    if (!currentVideo) return null;
    const stepsLike = isStepsLikeTutorial(currentVideo);
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-3',
          stepsLike ? 'flex-nowrap' : 'flex-wrap'
        )}
        data-testid="help-tutorial-header-nav"
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={footerPrevDisabled}
          onClick={handleFooterPrev}
          className={cn(
            'border-border bg-transparent text-foreground hover:bg-accent disabled:opacity-40',
            stepsLike && 'h-9 w-[9.25rem] shrink-0 justify-center px-2'
          )}
        >
          <ChevronRight size={14} className="mr-1 shrink-0 rotate-180" />
          Précédent
        </Button>
        {stepsLike ? (
          <Button
            type="button"
            size="sm"
            onClick={handleFooterNext}
            className={cn(
              'h-9 min-w-[12.75rem] shrink-0 justify-center px-3 text-primary-foreground hover:bg-primary/90',
              stepNavInfo.isLast ? 'bg-primary font-semibold' : 'bg-primary font-medium'
            )}
          >
            {stepNavInfo.isLast ? (
              'Parcours terminé'
            ) : (
              <>
                Suivant
                <ChevronRight size={16} className="ml-1 shrink-0" />
              </>
            )}
          </Button>
        ) : playlist && playlist.length > 1 && playlistIndex < playlist.length - 1 ? (
          <Button
            type="button"
            size="sm"
            onClick={handleFooterNext}
            className="bg-primary font-medium text-primary-foreground hover:bg-primary/90"
          >
            Suivant
            <ChevronRight size={16} className="ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={handleFooterNext}
            className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Parcours terminé
          </Button>
        )}
      </div>
    );
  };

  if (loadError) {
    return (
      <div className="flex h-dvh max-h-dvh flex-col items-center justify-center gap-4 overflow-hidden bg-background px-6 text-center text-foreground">
        <p className="text-sm">{loadError}</p>
        <Button type="button" variant="outline" onClick={handleLeave}>
          Fermer
        </Button>
      </div>
    );
  }

  if (tutorialFinished) {
    return (
      <div className="flex h-dvh max-h-dvh flex-col items-center justify-center gap-4 overflow-hidden bg-background px-6 text-center text-foreground">
        <p className="max-w-md text-sm leading-relaxed">
          Parcours terminé. Votre navigateur n&apos;a pas pu fermer cet onglet automatiquement : vous
          pouvez le fermer manuellement ou revenir au centre d&apos;aide depuis l&apos;application.
        </p>
        <Button type="button" variant="outline" onClick={handleLeave}>
          Fermer
        </Button>
      </div>
    );
  }

  if (autoClosePending) {
    return (
      <div
        className="flex h-dvh max-h-dvh flex-col items-center justify-center gap-3 overflow-hidden bg-background px-6 text-center text-foreground"
        aria-live="polite"
      >
        <div className="spinner" aria-hidden />
        <p className="text-sm text-muted-foreground">Fermeture de l&apos;onglet…</p>
      </div>
    );
  }

  if (!currentVideo || String(currentVideo.id) !== String(decodedId)) {
    return (
      <div className="flex h-dvh max-h-dvh items-center justify-center overflow-hidden bg-background text-foreground">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div
      className="flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden bg-background text-foreground"
      data-testid="help-tutorial-window"
    >
      {isStepsLikeTutorial(currentVideo) ? (
        <header className="relative shrink-0 border-b border-border bg-background px-3 py-2 sm:px-4 sm:py-2.5">
          <div className="grid grid-cols-1 items-start gap-2 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_auto] md:items-center md:gap-x-3">
            <div className="min-w-0 space-y-0.5 pr-10 text-left md:pr-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <h1 className="line-clamp-2 text-sm font-semibold leading-tight text-foreground sm:text-base">
                    {currentVideo.titre}
                  </h1>
                  <Eye size={16} className="shrink-0 text-muted-foreground" aria-hidden />
                </div>
                {isTutorialViewed(currentVideo.id) ? (
                  <span className="shrink-0 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 sm:text-xs">
                    Parcours terminé
                  </span>
                ) : null}
              </div>
              {currentThemeLabel ? (
                <p className="text-xs text-muted-foreground sm:text-sm">Thème : {currentThemeLabel}</p>
              ) : null}
              {currentVideo.description ? (
                <p className="line-clamp-2 text-xs leading-snug text-muted-foreground sm:text-sm">
                  {currentVideo.description}
                </p>
              ) : null}
              <div className="pt-0.5 md:hidden">
                {stepMeta.total > 0 ? (
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Étape {stepMeta.current} / {stepMeta.total}
                  </p>
                ) : null}
                {stepMeta.description ? (
                  <p className="line-clamp-2 text-xs leading-snug text-foreground">{stepMeta.description}</p>
                ) : null}
              </div>
            </div>
            <div className="hidden min-w-0 md:block md:border-l md:border-border md:pl-3 md:text-center">
              {stepMeta.total > 0 ? (
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Étape {stepMeta.current} / {stepMeta.total}
                </p>
              ) : null}
              <p className="mt-0.5 line-clamp-3 text-left text-xs leading-snug text-foreground md:text-center">
                {stepMeta.description || '\u00a0'}
              </p>
            </div>
            <div className="absolute right-2 top-2 z-10 md:static md:self-center md:justify-self-end">
              <button
                type="button"
                onClick={handleLeave}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-muted-foreground/70 hover:bg-accent hover:text-foreground"
                data-testid="help-tutorial-window-close"
                aria-label="Fermer"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
          </div>
        </header>
      ) : (
        <header className="relative shrink-0 overflow-y-auto border-b border-border bg-background px-4 pb-3 pt-4 sm:px-6 sm:pt-5">
          <div className="grid grid-cols-1 gap-y-4 md:grid-cols-[1fr_2fr_1fr] md:items-start md:gap-x-6 md:gap-y-0">
            <div className="min-w-0 space-y-1 pr-11 text-left md:pr-0">
              <div className="flex flex-wrap items-start gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <h1 className="line-clamp-2 text-base font-semibold leading-snug text-foreground sm:text-lg">
                    {currentVideo.titre}
                  </h1>
                  <Eye size={16} className="shrink-0 text-muted-foreground" aria-hidden />
                </div>
                {isTutorialViewed(currentVideo.id) ? (
                  <span className="shrink-0 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    Parcours terminé
                  </span>
                ) : null}
              </div>
              {currentThemeLabel ? (
                <p className="text-sm font-normal text-muted-foreground">Thème : {currentThemeLabel}</p>
              ) : null}
              {currentVideo.description ? (
                <p className="line-clamp-4 text-sm font-normal leading-snug text-muted-foreground sm:line-clamp-5">
                  {currentVideo.description}
                </p>
              ) : null}
              {playlist && playlist.length > 1 ? (
                <div className="space-y-0.5 text-xs font-normal text-muted-foreground">
                  <p>Vidéo {playlistIndex + 1} sur {playlist.length}</p>
                  <p>
                    Étape {stepNavInfo.current} / {stepNavInfo.total}
                  </p>
                </div>
              ) : null}
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-3 border-t border-border pt-3 md:border-l md:border-r md:border-t-0 md:border-border md:pl-6 md:pr-6 md:pt-0">
              {renderHeaderNav()}
            </div>
            <div className="absolute right-3 top-3 z-10 md:static md:self-start md:justify-self-end md:pt-0">
              <button
                type="button"
                onClick={handleLeave}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-muted-foreground/70 hover:bg-accent hover:text-foreground"
                data-testid="help-tutorial-window-close"
                aria-label="Fermer"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
          </div>
        </header>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {isStepsLikeTutorial(currentVideo) ? (
          <>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pt-1 sm:px-4 sm:pt-2">
              <StepsPlayer
                ref={stepsPlayerRef}
                hideInternalNav
                layoutCard
                compactLayout={false}
                fitViewport
                externalizeStepMeta
                onStepMetaChange={setStepMeta}
                initialStepIndex={initialStepIndex}
                onNavStateChange={setStepNavInfo}
                tutorial={currentVideo}
                onComplete={handleVideoComplete}
                onClose={closeVideoPlayer}
                onReachedLastStep={() => currentVideo?.id && markAsViewed(currentVideo.id)}
              />
            </div>
            <footer
              className="sticky bottom-0 z-20 shrink-0 border-t border-border bg-background/95 px-3 py-2.5 shadow-[0_-6px_16px_-4px_rgba(0,0,0,0.08)] backdrop-blur-sm dark:bg-background/95 dark:shadow-[0_-6px_20px_-4px_rgba(0,0,0,0.5)] sm:px-4"
              style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))' }}
              data-testid="help-tutorial-step-footer"
            >
              {renderHeaderNav()}
            </footer>
          </>
        ) : (
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-black">
            <div className="relative h-full min-h-0 w-full flex-1">
              <div className="relative h-full min-h-0 w-full max-h-[min(82dvh,calc(100dvh-10rem))]">
                <YouTubePlayerWithComments
                  tutorial={currentVideo}
                  onComplete={handleVideoComplete}
                  onClose={closeVideoPlayer}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
